import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import type { AxiosError } from 'axios';
import AuthLayout from '@/components/Auth/AuthLayout.vue';
import AuthCardHeader from '@/components/Auth/AuthCardHeader.vue';
import EmailStep from '@/components/Auth/EmailStep.vue';
import OrDivider from '@/components/Auth/OrDivider.vue';
import StoreDescription from './components/StoreDescription.vue';
import SignUpForm from '@/components/Auth/SignUpForm.vue';
import SocialAuthButton from '@/components/Auth/SocialAuthButton.vue';
import CAlert from '@/components/shared/CAlert.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import CError from '@/components/shared/CError.vue';
import { externalLogin } from '@/api/auth';
import { createCheckoutSession } from '@/api/billing';
import { useExternalAuth, type AuthResultHandled } from '@/composables/externalAuth';
import { externalAuthErrorMessage, isExpectedAuthError } from '@/utils/authUtils';
import { notifyAngularSignIn } from '@/utils/userUtils';
import { appStoreListings, getStoreDescription, normalizeStoreType } from './storeTypes';
import { ExternalAuthOutcome, type ExternalAuthResult, type ExternalProvider } from '@/typings/auth/externalAuth';

type Step = 'choice' | 'form' | 'email' | 'verify';
type PrefilledSignUp = {
    provider: ExternalProvider;
    pendingTicket: string;
    email: string;
    username?: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUp',
  setup(__props) {

const { authenticate, loadingProvider } = useExternalAuth();

const DEFAULT_SIGNUP_STORE_TYPE = 'Custom';
const searchParams = new URLSearchParams(window.location.search);
const requestedStoreType = normalizeStoreType(searchParams.get('storeType')) ?? DEFAULT_SIGNUP_STORE_TYPE;
const managedBy = searchParams.get('mngBy') ?? undefined;
// Partner managed accounts are always Custom stores, created through the manual form only
const isManagedByChipAdmin = managedBy === 'chipadmin';
const storeType = ref(isManagedByChipAdmin ? 'Custom' : requestedStoreType);

// Stripe sends a cancelled sign up checkout back here (see ExternalAuthBL.CreateCheckoutSession)
const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

// App store platforms only onboard from their store, a provider sign up would bypass it
const provider = computed(() =>
    isManagedByChipAdmin || appStoreListings[storeType.value] ? undefined : getStoreDescription(storeType.value).provider
);
const providerLabel = computed(() => (provider.value === 'etsy' ? 'Etsy' : 'Google'));

// App store platforms have nothing to choose: the form shows their install link
const step = ref<Step>(appStoreListings[storeType.value] ? 'form' : 'choice');
const pendingTicket = ref('');
const prefilled = ref<PrefilledSignUp | undefined>(undefined);
const error = ref('');

const cardTitle = computed(() => (step.value === 'verify' ? 'Check your email' : 'Register your new account'));

const signUpWith = async (provider: ExternalProvider) => {
    error.value = '';
    try {
        await authenticate(provider, 'signup', { storeType: storeType.value, managedBy }, onSignUpCompleted);
    } catch (authError) {
        // A closed popup or a code the user can act on is not a failure to report
        if ((authError as AxiosError)?.isAxiosError && !isExpectedAuthError(authError)) {
            window.rumReportError(authError as AxiosError);
        }
        error.value = externalAuthErrorMessage(authError, 'We could not sign you up. Please try again.');
    }
};

const onSignUpCompleted = async (result: ExternalAuthResult): Promise<AuthResultHandled> => {
    switch (result.outcome) {
        case ExternalAuthOutcome.FormPrefilled:
            // The provider covered part of the form, the user fills in the rest before the checkout
            pendingTicket.value = result.pendingTicket || '';
            prefilled.value = {
                provider: provider.value || 'google',
                pendingTicket: result.pendingTicket || '',
                email: result.email || '',
                username: result.username
            };
            step.value = 'form';
            return;
        case ExternalAuthOutcome.EmailRequired:
            pendingTicket.value = result.pendingTicket || '';
            step.value = 'email';
            return;
        case ExternalAuthOutcome.CheckoutRequired:
            // Paying is what verifies the account: the checkout success page opens the app
            if (result.checkoutUrl) {
                window.location.assign(result.checkoutUrl);
                return 'navigating';
            }
            return;
        default:
            if (result.loginTicket) {
                await externalLogin(result.loginTicket);
                notifyAngularSignIn();
            }
    }
};

const onFormBack = () => {
    // Leaving a prefilled form drops the provider sign up: starting it again is one click
    prefilled.value = undefined;
    pendingTicket.value = '';
    step.value = 'choice';
};

const registeredAccount = ref<Record<'username' | 'storeType', string>>();
const onManualRegistered = (username: string, storeType: string) => {
    registeredAccount.value = { username, storeType };
    step.value = 'verify';
    return openCheckout();
};

const openingCheckout = ref(false);

const openCheckout = async () => {
    if (!registeredAccount.value || openingCheckout.value) return;

    error.value = '';
    openingCheckout.value = true;
    try {
        const { username, storeType } = registeredAccount.value;
        window.location.assign(await createCheckoutSession(username, storeType));
    } catch (checkoutError) {
        // The account already exists at this point, only the checkout redirect failed
        window.rumReportError(checkoutError as AxiosError);
        error.value = externalAuthErrorMessage(checkoutError, 'We could not open the checkout. Please try again.');
        // Only a failure re-enables "Try again": on success the page is leaving for Stripe
        openingCheckout.value = false;
    }
};

return { __sfc: true,authenticate, loadingProvider, DEFAULT_SIGNUP_STORE_TYPE, searchParams, requestedStoreType, managedBy, isManagedByChipAdmin, storeType, checkoutCancelled, provider, providerLabel, step, pendingTicket, prefilled, error, cardTitle, signUpWith, onSignUpCompleted, onFormBack, registeredAccount, onManualRegistered, openingCheckout, openCheckout, AuthLayout, AuthCardHeader, EmailStep, OrDivider, StoreDescription, SignUpForm, SocialAuthButton, CAlert, CButton, CError }
}

})